import { ROLE_ID } from '@/constants/roles';

const DEFAULT_FALLBACK = '/dashboard';

/** Admin: restricted set (same paths as insurance-health-frontend). */
const ADMIN_ALLOWED_PATHS = [
  '/read-only-proposal-details',
  '/dashboard',
  '/welcome',
  '/login',
  '/user-management',
  '/sp-management',
  '/error-page',
];

let _registeredStaticPaths = null;
let _roleRouteAccessMap = null;

/**
 * Call once from router entry with the same `appRoutes` array passed to `createRouter`.
 * Derives static paths (omits `/:param` and catch-all routes).
 * @param {import('vue-router').RouteRecordRaw[]} appRoutes
 */
export function registerAppRoutesForRoleAccess(appRoutes) {
  _registeredStaticPaths = (appRoutes || [])
    .map((r) => r.path)
    .filter((path) => typeof path === 'string' && path.length > 0 && !path.includes('/:'));
  _roleRouteAccessMap = null;
}

function getRegisteredStaticPaths() {
  return _registeredStaticPaths || [];
}

/**
 * Static paths derived from the route table registered via {@link registerAppRoutesForRoleAccess}.
 * @returns {string[]}
 */
export function getAllAppRoutePaths() {
  return [...getRegisteredStaticPaths()];
}

/**
 * Full static paths except `/sp-management`.
 * @returns {string[]}
 */
export function getAllAppRoutePathsExcludingSpManagement() {
  return getRegisteredStaticPaths().filter((p) => p !== '/sp-management');
}

function buildRoleRouteAccessMap() {
  const nonAdminPaths = getAllAppRoutePathsExcludingSpManagement();
  return {
    [ROLE_ID.CONSUMER]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.RM]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.ADMIN]: {
      allowedPaths: [...ADMIN_ALLOWED_PATHS],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.POSP]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.DATA_OPERATOR]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.TELE_OPERATOR]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.AV]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.LG]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
    [ROLE_ID.TELE_MARKETER]: {
      allowedPaths: [...nonAdminPaths],
      fallbackPath: DEFAULT_FALLBACK,
    },
  };
}

function getRoleRouteAccessMap() {
  if (!_roleRouteAccessMap) {
    _roleRouteAccessMap = buildRoleRouteAccessMap();
  }
  return _roleRouteAccessMap;
}

/**
 * @returns {string|null}
 */
export function getRoleIdFromSessionStorage() {
  try {
    const raw = sessionStorage.getItem('rm_data');
    if (!raw) return null;
    const rmData = JSON.parse(raw);
    return rmData?.RoleId != null ? String(rmData.RoleId) : null;
  } catch {
    return null;
  }
}

/**
 * Restrict navigation for roles present in the access map (built from registered router paths).
 * Roles 7 and 8 are not in the map — unrestricted.
 * @param {import('vue-router').RouteLocationNormalized} to
 * @param {import('vue-router').RouteLocationNormalized} from
 * @param {import('vue-router').NavigationGuardNext} next
 */
export function runRoleRestrictedRouteGuard(to, from, next) {
  const roleId = getRoleIdFromSessionStorage();
  const ROLE_ROUTE_ACCESS = getRoleRouteAccessMap();
  const access = roleId ? ROLE_ROUTE_ACCESS[roleId] : null;

  if (!access) {
    next();
    return;
  }

  const allowed = access.allowedPaths || [];
  if (allowed.includes(to.path)) {
    next();
    return;
  }

  if (from?.path && from.path !== to.path && allowed.includes(from.path)) {
    next(false);
    return;
  }

  next(access.fallbackPath || DEFAULT_FALLBACK);
}
